/**
 * validate.ts — Validação de documento e de publicação
 *
 * Duas validações distintas:
 *  1. Estrutural: o documento é um schema válido, com blocos conhecidos.
 *  2. Publicação: o modelo entrega as CAPACIDADES exigidas pelo tipo e usa
 *     apenas blocos compatíveis com as saídas escolhidas.
 *
 * RELEASE: v1.2.0 - 2026-09-11 - Confere os espaços obrigatórios das composições (Fase 8)
 * RELEASE: v1.1.0 - 2026-09-11 - Orçamento de desempenho e de interação aplicado
 *            na publicação: avisa e bloqueia acúmulo de efeito caro (Fase 7)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 1)
 */

import { capabilitiesOf, getBlock } from './blockRegistry';
import { checkCompositionSlots } from './compositions/compositionRegistry';
import { avaliarOrcamento } from './screen';
import { getTemplateDefinition } from './templateDefinitions';
import {
  STUDIO_SCHEMA_VERSION,
  type StudioBlockNode,
  type StudioDocument,
  type StudioOutput,
  type StudioTemplateType,
  type StudioValidationIssue,
  type StudioValidationResult,
} from './types';

/** Percorre a árvore e devolve todos os nós em lista plana. */
export function flattenBlocks(blocks: StudioBlockNode[]): StudioBlockNode[] {
  const saida: StudioBlockNode[] = [];
  const visitar = (nos: StudioBlockNode[]) => {
    nos.forEach((no) => {
      saida.push(no);
      if (no.children?.length) visitar(no.children);
    });
  };
  visitar(blocks ?? []);
  return saida;
}

/**
 * Motivos pelos quais um documento é recusado (ou apenas avisado).
 * Publicado no catálogo para que uma IA saiba de antemão o que evitar,
 * em vez de descobrir errando.
 */
export const STUDIO_VALIDATION_CODES = [
  { code: 'documento_invalido', blocking: true, meaning: 'O conteúdo enviado não é um documento.' },
  { code: 'schema_ausente', blocking: true, meaning: 'Faltou `schemaVersion` (número) no envelope.' },
  { code: 'schema_futuro', blocking: true, meaning: '`schemaVersion` maior do que o sistema entende.' },
  { code: 'blocos_invalidos', blocking: true, meaning: '`blocks` precisa ser uma lista.' },
  { code: 'bloco_sem_id', blocking: true, meaning: 'Todo bloco precisa de `id`.' },
  { code: 'bloco_id_duplicado', blocking: true, meaning: 'Dois blocos com o mesmo `id`.' },
  { code: 'bloco_desconhecido', blocking: true, meaning: '`type` fora do catálogo de blocos.' },
  { code: 'bloco_nao_container', blocking: true, meaning: 'Blocos dentro de um bloco que não aceita filhos (`container: false`).' },
  { code: 'tipo_desconhecido', blocking: true, meaning: 'Tipo de modelo inexistente.' },
  { code: 'saida_nao_permitida', blocking: true, meaning: 'Forma de entrega que o tipo do modelo não aceita.' },
  { code: 'bloco_incompativel_saida', blocking: true, meaning: 'Bloco que não sabe se desenhar em uma das saídas escolhidas.' },
  { code: 'capacidade_ausente', blocking: true, meaning: 'Falta um bloco que entregue capacidade obrigatória do tipo.' },
  { code: 'espaco_obrigatorio_vazio', blocking: true, meaning: 'Espaço obrigatório de uma composição ficou sem conteúdo.' },
  { code: 'orcamento_excedido', blocking: true, meaning: 'Efeito/movimento acima do teto de bloqueio da página.' },
  { code: 'orcamento_alerta', blocking: false, meaning: 'Aviso de página pesada; ainda publica.' },
] as const;

/** Valida a estrutura do documento (schema e blocos conhecidos). */
export function validateDocument(doc: StudioDocument): StudioValidationResult {
  const issues: StudioValidationIssue[] = [];

  if (!doc || typeof doc !== 'object') {
    return { valid: false, issues: [{ code: 'documento_invalido', message: 'Documento inválido.' }] };
  }

  if (typeof doc.schemaVersion !== 'number') {
    issues.push({ code: 'schema_ausente', message: 'Documento sem versão de schema.' });
  } else if (doc.schemaVersion > STUDIO_SCHEMA_VERSION) {
    issues.push({
      code: 'schema_futuro',
      message: `Este modelo usa uma versão de schema mais nova (${doc.schemaVersion}) do que o sistema entende (${STUDIO_SCHEMA_VERSION}).`,
    });
  }

  if (!Array.isArray(doc.blocks)) {
    issues.push({ code: 'blocos_invalidos', message: 'Lista de blocos inválida.' });
    return { valid: false, issues };
  }

  const identificadores = new Set<string>();
  flattenBlocks(doc.blocks).forEach((no) => {
    if (!no.id) {
      issues.push({ code: 'bloco_sem_id', message: 'Existe bloco sem identificador.' });
    } else if (identificadores.has(no.id)) {
      issues.push({ code: 'bloco_id_duplicado', message: `Identificador de bloco repetido: ${no.id}.`, blockId: no.id });
    } else {
      identificadores.add(no.id);
    }

    const definicao = getBlock(no.type);
    if (!definicao) {
      issues.push({ code: 'bloco_desconhecido', message: `Bloco desconhecido: ${no.type}.`, blockId: no.id });
      return;
    }
    if (no.children?.length && !definicao.container) {
      issues.push({ code: 'bloco_nao_container', message: `O bloco ${definicao.label} não aceita blocos dentro dele.`, blockId: no.id });
    }
  });

  return { valid: issues.length === 0, issues };
}

/**
 * Valida se o modelo pode ser publicado no tipo e nas saídas informadas.
 * A checagem é por CAPACIDADE entregue, não por chave de bloco específica.
 */
export function validateForPublish(
  doc: StudioDocument,
  type: StudioTemplateType,
  outputs: StudioOutput[],
): StudioValidationResult {
  const estrutural = validateDocument(doc);
  const issues: StudioValidationIssue[] = [...estrutural.issues];

  const definicao = getTemplateDefinition(type);
  if (!definicao) {
    issues.push({ code: 'tipo_desconhecido', message: `Tipo de modelo desconhecido: ${type}.` });
    return { valid: false, issues };
  }

  const saidasInvalidas = outputs.filter((o) => !definicao.outputs.includes(o));
  if (saidasInvalidas.length) {
    issues.push({
      code: 'saida_nao_permitida',
      message: `Forma de entrega não permitida para este tipo: ${saidasInvalidas.join(', ')}.`,
    });
  }

  const nos = flattenBlocks(doc.blocks ?? []);
  const chaves = nos.map((n) => n.type);

  // Blocos precisam saber se desenhar em todas as saídas escolhidas
  nos.forEach((no) => {
    const bloco = getBlock(no.type);
    if (!bloco) return;
    const incompativeis = outputs.filter((o) => !bloco.outputs.includes(o));
    if (incompativeis.length) {
      issues.push({
        code: 'bloco_incompativel_saida',
        message: `O bloco ${bloco.label} não é compatível com: ${incompativeis.join(', ')}.`,
        blockId: no.id,
      });
    }
  });

  // Capacidades obrigatórias do tipo
  const entregues = capabilitiesOf(chaves);
  definicao.requiresCapabilities.forEach((cap) => {
    if (!entregues.has(cap)) {
      issues.push({
        code: 'capacidade_ausente',
        message: `Falta no modelo um bloco que entregue: ${cap}.`,
      });
    }
  });

  // Espaços obrigatórios das composições (Fase 8): se o usuário apagou o
  // título ou o botão de uma composição, a publicação avisa antes de sair.
  checkCompositionSlots(doc.blocks ?? []).forEach((problema) => {
    issues.push({
      code: 'espaco_obrigatorio_vazio',
      message: problema.message,
      ...(problema.blockId ? { blockId: problema.blockId } : {}),
    });
  });

  // Orçamento de desempenho e de interação (Fase 7): a conta é da página
  // inteira. Acima do teto de aviso o Studio apenas avisa; acima do teto de
  // bloqueio a publicação não sai.
  const orcamento = avaliarOrcamento(nos.map((no) => no.props));
  orcamento.mensagens.forEach((mensagem) => {
    issues.push({
      code: orcamento.bloqueia ? 'orcamento_excedido' : 'orcamento_alerta',
      message: mensagem,
    });
  });

  // Aviso não impede publicar: só o estouro do teto de bloqueio impede.
  const impeditivos = issues.filter((problema) => problema.code !== 'orcamento_alerta');
  return { valid: impeditivos.length === 0, issues };
}
